import {
  DEFAULT_LANGUAGE,
  LOCAL_STORAGE_KEY,
  PREFERRED_LANGUAGE_DEAL_SYNC_COMPLETED_KEY,
  PREFERRED_LANGUAGE_USER_SELECTED_KEY,
  SUPPORTED_LANGUAGES,
} from "utilities/Constant";
import type { BorrowerPersonalInformation } from "pages/dashboard/home/dashboardService";


import type { MyLoanInfo } from "pages/application/applicationService";

export function selectActiveLoan(
  loans: MyLoanInfo[] | undefined,
  nhDealApplicationId?: string | null,
): MyLoanInfo | undefined {
  if (!loans?.length) {
    return undefined;
  }

  if (nhDealApplicationId) {
    return loans.find((loan) => loan.nhDealApplicationId === nhDealApplicationId) ?? loans[0];
  }

  return loans[0];
}

export type ResolveDisplayedLanguageOptions = {
  isExplicitSelection: boolean;
  isMyLoansPage: boolean;
  hasApplicationContext: boolean;
  userDbLanguage?: string | null;
  activeLoan?: Pick<MyLoanInfo, "dealPreferredLanguage" | "loanNumber"> | null;
  reduxLanguage?: string | null;
};

export function resolveDisplayedPreferredLanguage(options: ResolveDisplayedLanguageOptions): string {
  const {
    isExplicitSelection,
    isMyLoansPage,
    hasApplicationContext,
    userDbLanguage,
    activeLoan,
    reduxLanguage,
  } = options;

  if (isExplicitSelection) {
    return getPreferredLanguage(
      getPersistedPreferredLanguage() ?? getExplicitUserLanguage() ?? reduxLanguage,
    );
  }

  if (hasApplicationContext && activeLoan) {
    return normalizeToAppLanguage(activeLoan.dealPreferredLanguage);
  }

  if (isMyLoansPage) {
    return normalizeToAppLanguage(userDbLanguage);
  }

  return getPreferredLanguage(reduxLanguage);
}

export function getPersistedPreferredLanguage(): string | null {
  const saved = globalThis.sessionStorage.getItem(LOCAL_STORAGE_KEY);
  return saved?.trim() ? normalizeToAppLanguage(saved) : null;
}

export function setPersistedPreferredLanguage(value: string): void {
  globalThis.sessionStorage.setItem(LOCAL_STORAGE_KEY, normalizeToAppLanguage(value));
}

export function extractDealServicePreferredLanguage(data?: BorrowerPersonalInformation): string | null {
  const preferredLanguage = data?.preferredLanguage;

  const value = preferredLanguage?.languageType;

  return value?.trim() ? value.trim() : null;
}

export function normalizeToAppLanguage(value?: string | null): string {
  if (!value?.trim()) {
    return DEFAULT_LANGUAGE;
  }

  switch (value.trim().toLowerCase()) {
    case "english":
      return "English";
    case "spanish":
    case "español":
    case "espanol":
      return "Español";
    default:
      return SUPPORTED_LANGUAGES.includes(value) ? value : DEFAULT_LANGUAGE;
  }
}

export function toUserDbPreferredLanguage(appLanguage: string): string {
  return normalizeToAppLanguage(appLanguage) === "Español" ? "Spanish" : "English";
}

export function resolvePersistedPreferredLanguage(savedLanguage?: string | null, fallbackLanguage?: string | null): string | undefined {
  if (savedLanguage?.trim()) {
    return normalizeToAppLanguage(savedLanguage);
  }

  if (fallbackLanguage?.trim()) {
    return normalizeToAppLanguage(fallbackLanguage);
  }

  return undefined;
}

export function getPreferredLanguage(reduxLanguage?: string | null): string {
  return normalizeToAppLanguage(
    getPersistedPreferredLanguage() ?? reduxLanguage ?? DEFAULT_LANGUAGE,
  );
}

export function isSpanishLanguage(value?: string | null): boolean {
  return normalizeToAppLanguage(value) === "Español";
}

export function hasExplicitUserLanguageSelection(): boolean {
  return globalThis.sessionStorage.getItem(PREFERRED_LANGUAGE_USER_SELECTED_KEY) === "true";
}

export function markExplicitUserLanguageSelection(): void {
  globalThis.sessionStorage.setItem(PREFERRED_LANGUAGE_USER_SELECTED_KEY, "true");
}

export function getExplicitUserLanguage(): string | null {
  if (!hasExplicitUserLanguageSelection()) {
    return null;
  }

  return getPersistedPreferredLanguage();
}

export function isPreLosLoan(loanNumber?: string | null): boolean {
  return !loanNumber?.trim() || loanNumber === "TBD";
}

export function shouldUseSessionStorageForMyLoansPreLos(loanNumber?: string | null): boolean {
  return hasExplicitUserLanguageSelection() && isPreLosLoan(loanNumber);
}

export function resolveMyLoansPreferredLanguage(
  apiPreferredLanguage?: string | null,
  loanNumber?: string | null,
): string | undefined {
  if (shouldUseSessionStorageForMyLoansPreLos(loanNumber)) {
    return getPersistedPreferredLanguage() ?? undefined;
  }

  return apiPreferredLanguage?.trim()
    ? normalizeToAppLanguage(apiPreferredLanguage)
    : undefined;
}

export function hasPreferredLanguageDealSyncCompleted(): boolean {
  return globalThis.sessionStorage.getItem(PREFERRED_LANGUAGE_DEAL_SYNC_COMPLETED_KEY) === "true";
}

export function markPreferredLanguageDealSyncCompleted(): void {
  globalThis.sessionStorage.setItem(PREFERRED_LANGUAGE_DEAL_SYNC_COMPLETED_KEY, "true");
}

export function clearPreferredLanguageStorageOnLogout(): void {
  globalThis.sessionStorage.removeItem(LOCAL_STORAGE_KEY);
  globalThis.sessionStorage.removeItem(PREFERRED_LANGUAGE_USER_SELECTED_KEY);
  globalThis.sessionStorage.removeItem(PREFERRED_LANGUAGE_DEAL_SYNC_COMPLETED_KEY);
  globalThis.localStorage.removeItem(LOCAL_STORAGE_KEY);
}

/** @deprecated Use clearPreferredLanguageStorageOnLogout */
export function clearPreferredLanguageDealSyncCompleted(): void {
  clearPreferredLanguageStorageOnLogout();
}

export function buildSyncPreferredLanguageRequest(
  userId: string,
  appLanguage: string,
): { userId: string; preferredLanguage: string } {
  return {
    userId,
    preferredLanguage: toUserDbPreferredLanguage(appLanguage),
  };
}
